/**
 * Stream-text helpers for the chat hook (useChat): chunk concatenation,
 * sentence-boundary normalization, status-visibility pacing, activity-key
 * dedup/IDs, and the stream-enter-attr strip seam.
 *
 * isStrayPageFinalText is an intentional mirror of
 * apps/x-agent/src/routes/chat.ts — kept duplicated (web cannot import x-agent).
 */

const MIN_XAVIOR_STATUS_VISIBLE_MS = 700

export function appendAssistantChunk(
  existing: string,
  incoming: string,
  options?: { forceParagraphBreak?: boolean },
): string {
  if (!incoming) return existing
  if (!existing) return incoming

  let next = incoming
  if (options?.forceParagraphBreak) {
    const trimmed = next.trimStart()
    if (!trimmed) return existing
    return `${existing}\n\n${trimmed}`
  }

  const previousChar = existing[existing.length - 1] ?? ''
  const nextChar = next[0] ?? ''
  const missingBoundarySpace =
    previousChar !== ''
    && nextChar !== ''
    && !/\s/.test(previousChar)
    && !/\s/.test(nextChar)

  // If a hidden directive was removed between sentences, keep a natural boundary.
  if (missingBoundarySpace && /[.!?]/.test(previousChar) && /[A-Z]/.test(nextChar)) {
    next = ` ${next}`
  }

  return existing + next
}

export function normalizeAssistantSentenceBoundaries(text: string): string {
  if (!text) return text
  return text.replace(
    /([a-z0-9\)])\s+(It's|It’s|It is|They're|They’re|They are|This|That|These|Those)\b/g,
    '$1. $2',
  )
}

export async function waitForXaviorStatusVisibility(lastStatusAtMs: number, signal: AbortSignal): Promise<void> {
  if (!lastStatusAtMs) return
  const waitMs = MIN_XAVIOR_STATUS_VISIBLE_MS - (Date.now() - lastStatusAtMs)
  if (waitMs <= 0) return

  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, waitMs)

    const onAbort = () => {
      clearTimeout(timeout)
      signal.removeEventListener('abort', onAbort)
      resolve()
    }

    signal.addEventListener('abort', onAbort, { once: true })
  })
}

export function isGenericThinkingLabel(label: string | null | undefined): boolean {
  const normalized = (label || '').trim().toLowerCase()
  return normalized === 'thinking' || normalized === 'thinking...'
}

/**
 * Labels that are too vague to justify replacing a more informative status.
 * "Running..."/"Working..." are the generic gerund fallbacks (getToolLabel) for
 * unmapped tools and the `ui_action` label — too vague to overwrite a concrete
 * status, so the tool_start path suppresses them and keeps the prior pill.
 * ("Preparing..." was the former `ui_action` label; it was renamed to
 * "Working...", which this regex already covers — no separate token needed.)
 */
export function isUninformativeToolLabel(label: string | null | undefined): boolean {
  const normalized = (label || '').trim().toLowerCase()
  return /^(running|working)\.\.\.$/.test(normalized)
}

/**
 * Mirror of isStrayPageFinalText in apps/x-agent/src/routes/chat.ts.
 * When a webpage build's closing sentence gets chewed by the planning-chatter
 * filter, a trailing adverb/connective can survive as the only visible text —
 * "throughout", "also", etc. Reject those here too so a mid-route server that
 * didn't filter still doesn't render them to the user.
 */
const STRAY_PAGE_FINAL_WORDS = new Set<string>([
  'throughout', 'also', 'and', 'but', 'however', 'meanwhile',
  'therefore', 'overall', 'finally', 'additionally', 'ultimately',
  'anyway', 'so', 'thus', 'here', 'this',
])
const GOOD_SHORT_CLOSINGS = new Set<string>([
  'done', 'saved', 'restored', 'built', 'live', 'updated', 'reverted',
])
export function isStrayPageFinalText(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return true
  const normalized = trimmed.replace(/[.!?,;:]+$/g, '').toLowerCase()
  if (!normalized) return true
  if (STRAY_PAGE_FINAL_WORDS.has(normalized)) return true
  if (!/\s/.test(normalized) && normalized.length < 20) {
    return !GOOD_SHORT_CLOSINGS.has(normalized)
  }
  return false
}

/**
 * Normalize activity labels for phrase-level dedup. Strips articles,
 * the word "section", punctuation, and whitespace variation so that
 * "Building header...", "Building the header...", and
 * "Building Header section..." collapse to one timeline entry.
 */
export function normalizeActivityKey(label: string): string {
  return (label || '')
    .trim()
    .toLowerCase()
    .replace(/\b(the|a|an)\b/g, '')
    .replace(/\bsection\b/g, '')
    .replace(/["'.,!?:;()]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

let _activityCounter = 0
export function makeActivityId(): string {
  return `act-${Date.now()}-${++_activityCounter}`
}

/**
 * Strip the transient `data-xavior-stream-enter` marker from a cumulative
 * HTML string before persistence. The attribute is stamped on section frames
 * by the client so the iframe can run a fade-in transition, then removed by
 * the iframe's inspector script on the next rAF tick. If we ever save the
 * raw pre-stripped HTML and reload the page, every section would render
 * invisibly — belt-and-suspenders strip it here too.
 */
export function stripStreamEnterAttr(html: string): string {
  if (!html) return html
  // Remove attribute variants: `data-xavior-stream-enter`, possibly with
  // surrounding whitespace, quoted empty value, or trailing whitespace.
  return html.replace(/\s+data-xavior-stream-enter(?:="[^"]*")?/g, '')
}

/**
 * Repair a page build that was cut off before the document closed.
 *
 * When a build hits the LLM output-token ceiling mid-stream it never emits the
 * final `</body></html>` (often it's cut mid-`<script>`), and an unclosed
 * `<script>` swallows everything after it — the iframe renders blank. We were
 * silently persisting these, so the user got a broken page (the John Coltrane
 * build was cut off after the hero, leaving no music section and no close tags).
 *
 * This is a deterministic safety net at the persistence boundary: if a full
 * document was opened (`<html>`) but never closed (`</html>`), close any
 * dangling `<script>` then append `</body></html>` so the browser renders what
 * did arrive. Returns `wasTruncated` so the caller can flag/telemetry it.
 *
 * No-ops on section-only payloads (edits, patches) — those never open `<html>`.
 */
export function repairTruncatedHtml(html: string): { html: string; wasTruncated: boolean } {
  if (!html || !/<html[\s>]/i.test(html)) return { html, wasTruncated: false }
  // A genuinely complete document ends with </html>. Requiring it at the END
  // (not merely present anywhere) avoids a stray '</html>' inside a JS string or
  // comment masking a real truncation.
  if (/<\/html\s*>$/i.test(html.trimEnd())) return { html, wasTruncated: false }
  let repaired = html
  const opens = (repaired.match(/<script\b/gi) || []).length
  const closes = (repaired.match(/<\/script\s*>/gi) || []).length
  if (opens > closes) repaired += '\n</script>'
  if (!/<\/body\s*>/i.test(repaired)) repaired += '\n</body>'
  repaired += '\n<!-- xavior:auto-closed (truncated build) -->\n</html>'
  return { html: repaired, wasTruncated: true }
}
